import moment from 'moment'
import { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Brush, Customized, XAxis } from 'recharts'
import { ChartContainer } from '../../../../../../../common/chart/chartContainer'
import Bar from '../../../../../../../common/chart/chartContainer/Bar'
import Line from '../../../../../../../common/chart/chartContainer/Line'
import { Legend } from '../../../../../../../common/chart/legend'
import { Element } from '../../../../../../../common/html/element'
import { Loading } from '../../../../../../../common/loading'
import { Notification } from '../../../../../../../common/notification'
import { formatByTheme } from '../../../../../../../helpers/theme'
import { valToPercent } from '../../../../../../../helpers/value'
import { MARGIN_LEFT_CHART_DYNAMIC } from '../../../../../../../tool/dynamicReport/constant'
import { PERIOD_TYPE } from '../../../../../constant'
import {
  selectChartHistoricalChange,
  selectLoading,
  selectRiskScaleInterpretation,
} from '../../../../store/slice'
import { TYPE_CHART_TREND_OF_SCORE } from './constants'
import StackColorBarYAxis from './StackColorBarYAxis'

const pointRanges = [0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]

export const HistoricalChangeChart = ({
  isDynamicReport,
  settingDynamic,
  timeFrequency,
}) => {
  const isLoading = useSelector(selectLoading('historicalChange'))
  const ref = useRef()
  const chartHistoricalChange = useSelector(selectChartHistoricalChange)
  const locale = useSelector((state) => state.i18n.locale)

  const riskScaleInterpretation = useSelector(selectRiskScaleInterpretation)

  const [listCheckBox, setListCheckBox] = useState(
    isDynamicReport
      ? settingDynamic?.checkedHistoricalChange
      : TYPE_CHART_TREND_OF_SCORE.map((item) => item.dataKey),
  )

  //Footer
  const legendItems = [
    {
      title: 'corporateRiskScore.probabilityOfDefault',
      isI18n: true,
      color: TYPE_CHART_TREND_OF_SCORE[0].color,
      dataKey: 'data1',
      iconCustom: true,
      isCheckbox: true,
    },
    {
      title: 'corporateRiskScore.scoreCompany',
      isI18n: true,
      color: TYPE_CHART_TREND_OF_SCORE[1].color,
      dataKey: 'data2',
      isCheckbox: true,
    },
    {
      title: 'corporateRiskScore.scoreModelSegment',
      isI18n: true,
      color: TYPE_CHART_TREND_OF_SCORE[2].color,
      dataKey: 'data3',
      isCheckbox: true,
    },
  ]

  const actionCheckBox = (list) => {
    setListCheckBox(list)
  }

  const data = useMemo(() => {
    if (!chartHistoricalChange) return []
    return chartHistoricalChange.map((item) => ({
      ...item,
      data1: item.pd * 100,
      data2: item.companyScore,
      data3: item.modelSegmentScore,
      xAxis:
        timeFrequency === PERIOD_TYPE.YEARLY
          ? String(item.intYear)
          : timeFrequency === PERIOD_TYPE.QUARTERLY
          ? `Q${item.intQuarter}-${item.intYear}`
          : timeFrequency === PERIOD_TYPE.MONTHLY
          ? moment(`${item.intMonth}/${item.intYear}`, 'MM/YYYY').format(
              'MMM-YYYY',
            )
          : '',
    }))
  }, [chartHistoricalChange, timeFrequency])

  const maxPD = useMemo(() => {
    if (!data.length) return 0.1
    const max = Math.max(...data.map((d) => d.data1 || 0))
    const calculatedMax = max * 20
    return Math.min(calculatedMax, 100)
  }, [data])

  const pdTicks = useMemo(() => {
    const steps = 5
    return Array.from({ length: steps + 1 }, (_, i) =>
      Number(((maxPD / steps) * i).toFixed(2)),
    )
  }, [maxPD])

  if (isLoading)
    return (
      <div className="positionRelative h100">
        <Loading />
      </div>
    )

  if (!data.length) return <Notification />

  return (
    <>
      <div ref={ref} id="historicalChangeChartId">
        <Element
          style={{
            color: '#9C9C9C',
            lineHeight: '16px',
            padding: '4px 0',
          }}
        >
          <Translate value="riskScaleAndInterpretation.riskScore" />
        </Element>
        <div>
          <ChartContainer
            chartId="historicalChangeChartId"
            data={data}
            width="100%"
            height={280}
            margin={{
              left: isDynamicReport ? MARGIN_LEFT_CHART_DYNAMIC + 15 : 15,
              right: 10,
              bottom: 6,
            }}
            yAxis={
              listCheckBox.includes('data1')
                ? [
                    {
                      yAxisId: 'left',
                      dataKeys: ['data2', 'data3'],
                      ticks: pointRanges,
                      isLineChart: true,
                      tickMargin: 24,
                      tickFormatter: (value) =>
                        pointRanges.includes(value) ? value : '',
                    },
                    {
                      yAxisId: 'right',
                      orientation: 'right',
                      isBarChart: true,
                      dataKeys: ['data1'],
                      ticks: pdTicks,
                      tickFormatter: (value) => value + '%',
                    },
                  ]
                : [
                    {
                      yAxisId: 'left',
                      dataKeys: ['data2', 'data3'],
                      ticks: pointRanges,
                      isLineChart: true,
                      tickMargin: 24,
                      tickFormatter: (value) =>
                        pointRanges.includes(value) ? value : '',
                    },
                  ]
            }
            tooltip={{
              schema: {
                content: [
                  {
                    label: 'corporateRiskScore.probabilityOfDefault',
                    isI18n: true,
                    dataKey: 'data1',
                    formatValue: (value) =>
                      valToPercent(value, locale, true, true),
                  },
                  {
                    label: 'corporateRiskScore.scoreCompany',
                    isI18n: true,
                    dataKey: 'data2',
                  },
                  {
                    label: 'corporateRiskScore.scoreModelSegment',
                    isI18n: true,
                    dataKey: 'data3',
                  },
                ],
              },
            }}
          >
            <Customized
              component={<StackColorBarYAxis locale={locale} />}
              yAxisId="left"
              ranges={{
                data: riskScaleInterpretation,
              }}
            />
            <XAxis
              height={60}
              dataKey="xAxis"
              angle={timeFrequency !== PERIOD_TYPE.YEARLY ? -30 : ''}
              textAnchor="middle"
              padding={{ left: 14, right: 30 }}
              axisLine={{ stroke: formatByTheme('#D5D5D5') }}
              tickLine={false}
              stroke="#9C9C9C"
              tickMargin={timeFrequency !== PERIOD_TYPE.YEARLY ? 20 : 9}
              interval={0}
            />
            {TYPE_CHART_TREND_OF_SCORE.filter(
              (key) => listCheckBox.includes(key.dataKey) && key.isBarChart,
            ).map((item, index) => (
              <Bar
                key={index}
                yAxisId="right"
                dataKey={item.dataKey}
                fill={item.color}
                maxBarSize={35}
              />
            ))}
            {TYPE_CHART_TREND_OF_SCORE.filter(
              (key) => listCheckBox.includes(key.dataKey) && key.isLineChart,
            ).map((item) => (
              <Line
                key={item.id}
                yAxisId="left"
                dataKey={item.dataKey}
                stroke={item.color}
                strokeWidth={2}
              />
            ))}
            {timeFrequency !== PERIOD_TYPE.YEARLY && (
              <Brush
                className="customBrush"
                startIndex={data.length > 12 ? data.length - 12 : 0}
                endIndex={data.length > 12 ? data.length - 1 : data.length}
                height={6}
                stroke={isDynamicReport ? 'transparent' : '#BCBCBC'}
              />
            )}
          </ChartContainer>
        </div>
        <Legend
          items={legendItems}
          style={{ display: 'flex', justifyContent: 'center', gap: 40 }}
          listCheckBox={listCheckBox}
          setListCheckBox={actionCheckBox}
          chartId="historicalChangeChartId"
          isDynamicReport={isDynamicReport}
        />
      </div>
    </>
  )
}
